/**
 * FormField
 *
 * Reusable form field component that wraps inputs with labels, error messages,
 * helper text, and character counts. Provides consistent styling and accessibility.
 */

import React from 'react';

import { cn } from '@/lib/utils';

import { errorStyles, helperTextStyles, labelStyles } from './styles';

// ============================================================================
// Types
// ============================================================================

export interface CharacterCountConfig {
  current: number;
  max: number;
  /** Threshold (0-1) at which warning styling appears. Default: 0.8 */
  warningThreshold?: number;
}

export interface FormFieldProps {
  label: string;
  htmlFor: string;
  required?: boolean;
  error?: string;
  helperText?: string;
  /** Character count with optional warning threshold */
  characterCount?: CharacterCountConfig;
  /** Show label and character count on same row */
  inlineCount?: boolean;
  children: React.ReactNode;
  className?: string;
}

// ============================================================================
// Component
// ============================================================================

export const FormField: React.FC<FormFieldProps> = ({
  label,
  htmlFor,
  required = false,
  error,
  helperText,
  characterCount,
  inlineCount = false,
  children,
  className,
}) => {
  const errorId = error ? `${htmlFor}-error` : undefined;
  const helperTextId = helperText ? `${htmlFor}-helper` : undefined;
  const describedBy =
    [errorId, helperTextId].filter(Boolean).join(' ') || undefined;

  // Character count styling
  const charCountElement = characterCount && (
    <CharacterCount
      current={characterCount.current}
      max={characterCount.max}
      warningThreshold={characterCount.warningThreshold}
    />
  );

  return (
    <div className={cn('space-y-1', className)}>
      {/* Label Row (optionally with inline character count) */}
      {inlineCount && characterCount ? (
        <div className="flex items-center justify-between">
          <Label htmlFor={htmlFor} required={required}>
            {label}
          </Label>
          {charCountElement}
        </div>
      ) : (
        <Label htmlFor={htmlFor} required={required}>
          {label}
        </Label>
      )}

      {/* Input/Textarea (passed as children) */}
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child, {
            'aria-invalid': !!error,
            'aria-describedby': describedBy,
            'aria-required': required,
            ...child.props,
          } as React.HTMLAttributes<HTMLElement>);
        }
        return child;
      })}

      {/* Helper Text */}
      {helperText && !error && (
        <p id={helperTextId} className={helperTextStyles}>
          {helperText}
        </p>
      )}

      {/* Error Message */}
      {error && (
        <p id={errorId} role="alert" className={errorStyles}>
          {error}
        </p>
      )}

      {/* Character Count (standalone, not inline) */}
      {!inlineCount && charCountElement}
    </div>
  );
};

// ============================================================================
// Sub-components
// ============================================================================

interface LabelProps {
  htmlFor: string;
  required: boolean;
  children: React.ReactNode;
}

const Label: React.FC<LabelProps> = ({ htmlFor, required, children }) => (
  <label htmlFor={htmlFor} className={labelStyles}>
    {children}
    {required && (
      <span className="text-red-400 ml-0.5" aria-label="required">
        *
      </span>
    )}
  </label>
);

interface CharacterCountProps {
  current: number;
  max: number;
  warningThreshold?: number;
}

const CharacterCount: React.FC<CharacterCountProps> = ({
  current,
  max,
  warningThreshold = 0.8,
}) => {
  const isOverLimit = current > max;
  const isWarning = current >= max * warningThreshold && !isOverLimit;

  return (
    <span
      className={cn(
        'text-[10px] tabular-nums',
        isOverLimit && 'text-red-500 font-medium',
        isWarning && 'text-amber-500 font-medium',
        !isOverLimit && !isWarning && 'text-zinc-400',
      )}
    >
      {current} / {max}
    </span>
  );
};
